import Colors from '@root/joinroot.com/src/utils/colors';
import MapColorScale from '@root/joinroot.com/src/components/covid-driving/map-section/map-color-scale';
import MapTooltip from '@root/joinroot.com/src/components/covid-driving/map-section/map-tooltip';
import Market from '@root/core/src/models/market';
import PropTypes from '@root/vendor/prop-types';
import React, { useRef, useState } from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import Select from '@root/core/src/components/select';
import StateSVGData from '@root/joinroot.com/src/assets/data/driving-data/interactive-states';
import WashingtonDC from '@root/joinroot.com/src/components/covid-driving/map-section/interactive-washington-dc';
import { P1 } from '@root/joinroot.com/src/components/text';
import { StyleSheet } from '@root/core/src/utils/styles';

export default function InteractiveUSMap({ dataToDisplay, startDate }) {
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({});
  const [selectedState, setSelectedState] = useState();
  const tooltipRef = useRef(null);
  const interactiveMapRef = useRef(null);
  const mapContainerRef = useRef(null);

  const stateSelectOptions = Object.keys(dataToDisplay.states).map((stateId) => ({
    label: Market.MAPPING[stateId],
    value: stateId,
  }));

  function getPercentChangeClass(stateId) {
    if (dataToDisplay.states && dataToDisplay.states[stateId]) {
      const percentChange = dataToDisplay.states[stateId]['2020'].pctChange;

      switch (true) {
      case percentChange > 0:
        return 'percentChange-greaterThan0';
      case percentChange <= 0 && percentChange > -15:
        return 'percentChange-10';
      case percentChange <= -15 && percentChange > -20:
        return 'percentChange-15';
      case percentChange <= -20 && percentChange > -25:
        return 'percentChange-20';
      case percentChange <= -25 && percentChange > -30:
        return 'percentChange-25';
      case percentChange <= -30:
        return 'percentChange-30';
      default:
        return 'percentChange-notMeasured';
      }
    }
    return 'percentChange-notMeasured';
  }

  function handleMouseMove(e, state) {
    if (dataToDisplay.states && dataToDisplay.states[state.id.toUpperCase()]) {
      const formattedData = {
        state: state.id.toUpperCase(),
        startDate,
        ...dataToDisplay.states[state.id.toUpperCase()],
      };

      setTooltipData(formattedData);

      if (tooltipRef.current) {
        const containerXOffset = mapContainerRef.current.getBoundingClientRect().left;
        const mapYOffset = interactiveMapRef.current.getBoundingClientRect().top;
        const tooltipWidth = tooltipRef.current.getBoundingClientRect().width;

        const tooltipLeftPosition = Math.floor(e.clientX - containerXOffset - tooltipWidth / 2);
        const tooltipTopPosition = Math.floor(e.clientY - mapYOffset + 25);

        setTooltipPosition({
          left: tooltipLeftPosition,
          top: tooltipTopPosition,
        });
      }
    } else {
      setTooltipData(null);
    }
  }

  function handleStateChange(state) {
    setSelectedState(state);

    if (dataToDisplay.states && dataToDisplay.states[state]) {
      const formattedData = {
        state,
        startDate,
        ...dataToDisplay.states[state],
      };

      setTooltipData(formattedData);
    } else {
      setTooltipData(null);
    }
  }

  return (
    <div
      css={styles.interactiveMapContainer}
      ref={mapContainerRef}
    >
      <svg
        css={styles.interactiveMap}
        ref={interactiveMapRef}
        viewBox={'0 0 980 593'}
        xmlns={'http://www.w3.org/2000/svg'}
      >
        <g css={styles.states}>
          {StateSVGData.map((state, index) => {
            return (
              <g
                css={styles.stateGroup}
                data-state-id={state.id}
                data-testid={state.id}
                key={index}
                onMouseMove={(e) => handleMouseMove(e, state)}
                tabIndex={'-1'}
              >
                <path
                  className={`state ${getPercentChangeClass(state.id.toUpperCase())}`}
                  css={styles.state}
                  d={state.shape.dimensions}
                />
                {state.shape.labelLine &&
                  <path
                    css={styles.labelLine}
                    d={state.shape.labelLine.dimensions}
                    transform={'translate(0, -0.4)'}
                  />
                }
                <text
                  css={styles.mapText}
                  transform={`${state.shape.labelTransform}`}
                >
                  <tspan
                    dy={dataToDisplay.states && dataToDisplay.states[state.id.toUpperCase()] ? '-.6em' : 0}
                    x={0}
                  >{state.id.toUpperCase()}
                  </tspan>
                  {dataToDisplay.states && dataToDisplay.states[state.id.toUpperCase()] &&
                  <tspan
                    dy={'1.2em'}
                    x={0}
                  >
                    {Math.round(dataToDisplay.states[state.id.toUpperCase()]['2020'].pctChange)}%
                  </tspan>
                  }
                </text>
              </g>
            );
          })}

          <WashingtonDC
            dataToDisplay={dataToDisplay}
            onMouseMove={(e) => handleMouseMove(e, {
              id: 'DC',
            })}
            percentChangeClassName={getPercentChangeClass('DC')}
            styles={styles}
          />
        </g>
      </svg>
      <MapColorScale />
      <div css={styles.stateSelectContainer}>
        <P1 css={styles.stateSelectHeading}>See driving trends for</P1>
        <Select
          inputId={'map-state-select'}
          inputName={'map-state-select'}
          onChange={(e) => handleStateChange(e)}
          options={stateSelectOptions}
          placeholder={'Select a state'}
          selectedValue={selectedState || ''}
        />
      </div>
      <MapTooltip
        ref={tooltipRef}
        startDate={startDate}
        tooltipData={tooltipData}
        tooltipPosition={tooltipPosition}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  interactiveMapContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 1400,
    ...Responsive.md({
      position: 'relative',
    }),
  },
  interactiveMap: {
    width: '100%',
    maxWidth: 940,
    height: 'auto',
    overflow: 'visible',
  },
  stateGroup: {
    pointerEvents: 'all',
    '&:hover': {
      cursor: 'pointer',
      textDecoration: 'none',
    },
  },
  state: {
    fill: Colors.grayLight(),
    '&.percentChange-greaterThan0': {
      fill: Colors.lightBlue(),
    },
    '&.percentChange-10': {
      fill: Colors.rootOrange(.1),
    },
    '&.percentChange-15': {
      fill: Colors.rootOrange(.25),
    },
    '&.percentChange-20': {
      fill: Colors.rootOrange(.5),
    },
    '&.percentChange-25': {
      fill: Colors.rootOrange(.75),
    },
    '&.percentChange-30': {
      fill: Colors.rootOrange(),
    },
  },
  mapText: {
    fill: Colors.nearBlack(),
    textAnchor: 'middle',
    fontSize: 12,
    fontWeight: 600,
    ...Responsive.md({
      fontSize: 12,
    }),
  },
  labelLine: {
    strokeWidth: 1,
    stroke: Colors.nearBlack(),
  },
  stateSelectContainer: {
    margin: '30px auto',
    width: '100%',
    maxWidth: 640,
    ...Responsive.md({
      display: 'none',
    }),
  },
  stateSelectHeading: {
    marginBottom: 15,
  },
});

InteractiveUSMap.propTypes = {
  dataToDisplay: PropTypes.object.isRequired,
  startDate: PropTypes.instanceOf(Date),
};
